import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays } from "lucide-react";
import { POPOVER_VIEWPORT_PAD } from "./popoverLayout";

/** Themed date input. Renders a styled text field accepting
 * `YYYY-MM-DD` plus a calendar button that opens a brand-themed
 * month grid popover. Replaces the native `<input type="date">`
 * (forbidden by the no-native-chrome rule in AGENTS.md).
 *
 * Contract: controlled component. `value` is the ISO date string
 * (`"2026-05-18"`) or `""` for empty. `onChange` always emits a
 * string in the same shape, including `""` when the operator
 * clears the field.
 *
 * Typing into the field is allowed (legacy parity — operators used
 * to type the date directly). The calendar button is a one-click
 * shortcut for picking it visually.
 */
export type TkticDateInputProps = {
  value: string;
  onChange: (value: string) => void;
  /** Forwarded to the underlying input for analytics + tests. */
  id?: string;
  name?: string;
  "data-testid"?: string;
  /** Visible placeholder text. Defaults to the ISO hint operators
   * already see in the existing text-only inputs. */
  placeholder?: string;
  /** Disable both the text field and the calendar button. */
  disabled?: boolean;
  /** Min / max selectable date (ISO `YYYY-MM-DD`). Out-of-range
   * days render disabled in the picker; the text field still
   * accepts them (the form's validator owns rejection). */
  min?: string;
  max?: string;
  /** Optional extra classes appended to the input. */
  className?: string;
  /** ARIA-label for the calendar trigger button. */
  buttonAriaLabel?: string;
};

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** YYYY-MM-DD → Date | null. Returns null for empty / malformed. */
function parseIso(value: string): Date | null {
  if (!value || !ISO_RE.test(value)) return null;
  const [y, m, d] = value.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  if (
    dt.getFullYear() !== y ||
    dt.getMonth() !== m - 1 ||
    dt.getDate() !== d
  ) {
    return null;
  }
  return dt;
}

function formatIso(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function sameDay(a: Date | null, b: Date | null): boolean {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date: Date, n: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + n, 1);
}

const WEEKDAY_LABELS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function TkticDateInput({
  value,
  onChange,
  id,
  name,
  "data-testid": dataTestId,
  placeholder = "YYYY-MM-DD",
  disabled = false,
  min,
  max,
  className = "",
  buttonAriaLabel = "Open calendar",
}: TkticDateInputProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  // Position of the portaled popup (anchored to the trigger's right
  // edge). Recomputed on open and on scroll/resize while open so a
  // scroll-clipping ancestor (e.g. the Coach Review focus-mode tools
  // panel) doesn't crop the calendar.
  const [popupPos, setPopupPos] = useState<{ top: number; right: number } | null>(null);

  const selected = useMemo(() => parseIso(value), [value]);
  const minDate = useMemo(() => (min ? parseIso(min) : null), [min]);
  const maxDate = useMemo(() => (max ? parseIso(max) : null), [max]);
  const [viewMonth, setViewMonth] = useState<Date>(() =>
    startOfMonth(selected ?? new Date()),
  );

  // Re-sync the view to the selected date whenever it changes
  // (typing into the field jumps the calendar to that month on the
  // next open).
  useEffect(() => {
    if (selected) setViewMonth(startOfMonth(selected));
  }, [selected]);

  // Dismiss on outside click + Escape. The popup is portaled to
  // <body>, so the click-outside check must include the popup ref
  // alongside the container ref.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      const el = containerRef.current;
      const popup = popupRef.current;
      const target = e.target as Node;
      if (el && el.contains(target)) return;
      if (popup && popup.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onPointer);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onPointer);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Anchor the portaled popup to the trigger row's right edge
  // (matching the old `absolute right-0 mt-1` behavior, but in
  // viewport coords so a scroll-clipping ancestor can't crop it).
  useLayoutEffect(() => {
    if (!open) {
      setPopupPos(null);
      return;
    }
    const compute = () => {
      const el = containerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const viewportPad = POPOVER_VIEWPORT_PAD;
      setPopupPos({
        top: r.bottom + 4,
        right: Math.max(viewportPad, window.innerWidth - r.right),
      });
    };
    compute();
    window.addEventListener("scroll", compute, true);
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute, true);
      window.removeEventListener("resize", compute);
    };
  }, [open]);

  const grid = useMemo(() => buildMonthGrid(viewMonth), [viewMonth]);

  const inRange = (d: Date): boolean => {
    if (minDate && d < minDate) return false;
    if (maxDate && d > maxDate) return false;
    return true;
  };

  const pick = (d: Date) => {
    onChange(formatIso(d));
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          id={id}
          name={name}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          data-testid={dataTestId}
          className={`w-full min-w-0 rounded border border-rule bg-canvas pl-2 pr-8 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 ${className}`}
        />
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={disabled}
          aria-label={buttonAriaLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
          data-testid={dataTestId ? `${dataTestId}-calendar-trigger` : undefined}
          style={{
            backgroundColor: "var(--flag)",
            color: "var(--accent-ink)",
            borderColor: "var(--flag)",
          }}
          className="absolute right-0 top-0 bottom-0 flex w-7 items-center justify-center rounded-r border border-l-0 hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
        >
          <CalendarIcon />
        </button>
      </div>
      {open && popupPos && typeof document !== "undefined"
        ? createPortal(
        <div
          ref={popupRef}
          role="dialog"
          aria-label="Pick a date"
          data-tktic-popover="date"
          data-testid={dataTestId ? `${dataTestId}-calendar` : undefined}
          style={{ position: "fixed", top: popupPos.top, right: popupPos.right }}
          className="z-[200] w-[min(18rem,calc(100vw-1rem))] rounded border border-rule bg-surface p-3 shadow-lg"
        >
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setViewMonth((m) => addMonths(m, -1))}
              aria-label="Previous month"
              className="rounded border border-rule px-2 py-1 text-ink-muted hover:text-ink hover:border-accent transition-colors"
            >
              ‹
            </button>
            <span className="font-display uppercase tracking-wider text-xs text-ink">
              {MONTH_LABELS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
            </span>
            <button
              type="button"
              onClick={() => setViewMonth((m) => addMonths(m, 1))}
              aria-label="Next month"
              className="rounded border border-rule px-2 py-1 text-ink-muted hover:text-ink hover:border-accent transition-colors"
            >
              ›
            </button>
          </div>
          <div className="mt-2 grid grid-cols-7 gap-1 text-center text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
            {WEEKDAY_LABELS.map((w) => (
              <div key={w}>{w}</div>
            ))}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-1">
            {grid.map((day, idx) => {
              const isCurrentMonth = day.getMonth() === viewMonth.getMonth();
              const isSelected = sameDay(day, selected);
              const isToday = sameDay(day, new Date());
              const allowed = inRange(day);
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => allowed && pick(day)}
                  disabled={!allowed}
                  aria-pressed={isSelected}
                  aria-current={isToday ? "date" : undefined}
                  className={[
                    "h-8 rounded text-xs transition-colors",
                    isSelected
                      ? "text-ink"
                      : isCurrentMonth
                        ? "text-ink hover:bg-field"
                        : "text-ink-faint hover:bg-field",
                    !allowed ? "opacity-30 cursor-not-allowed" : "",
                    isToday && !isSelected ? "ring-1 ring-rule" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  style={
                    isSelected
                      ? {
                          backgroundColor: "var(--flag)",
                          color: "var(--accent-ink)",
                        }
                      : undefined
                  }
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex items-center justify-between gap-2 border-t border-rule pt-2">
            <button
              type="button"
              onClick={() => pick(new Date())}
              className="rounded border border-rule px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted hover:text-ink hover:border-accent transition-colors"
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              className="rounded border border-rule px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted hover:text-ink hover:border-accent transition-colors"
            >
              Clear
            </button>
          </div>
        </div>,
        document.body,
      )
        : null}
    </div>
  );
}

/** Build a 6-week (42-day) grid spanning the visible month, padded
 * with leading/trailing days from neighboring months so every cell
 * is filled. Week starts on Monday. */
function buildMonthGrid(viewMonth: Date): Date[] {
  const first = startOfMonth(viewMonth);
  // JS Sunday=0; shift so Monday=0.
  const leadingDays = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - leadingDays);
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

function CalendarIcon() {
  return <CalendarDays size={14} strokeWidth={2} aria-hidden="true" />;
}
